import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { RedirectError } from 'src/shared/error';

import type { PromotePageData } from '../types';

import { getComboPageBanditData } from './getComboPageBanditData';
import type { PromotePageMainDataParams } from './getPromotePageMainData';
import { getPromotePageMainData } from './getPromotePageMainData';
import type {
	PromotePageCountryConfigQuery,
	PromotePageCountryConfigQueryVariables,
} from './promotePageCountryConfig.graphql';
import { PromotePageCountryConfig } from './promotePageCountryConfig.graphql';
import { getAmountChoices } from './utilities/getAmountChoices';

export type { PromotePageMainDataQuery } from './getPromotePageMainData.graphql';

async function handleFcmRedirect(utilityContext: UtilityContext, params: PromotePageMainDataParams) {
	if (params.loginState === 'GUEST') return;
	const {
		data: {
			featureConfigs: { petitionPromotePageForCountry },
			viewer: { loginState },
		},
	} = await utilityContext.gql.fetch<PromotePageCountryConfigQuery, PromotePageCountryConfigQueryVariables>({
		query: PromotePageCountryConfig,
		rejectOnError: true,
	});
	if (loginState === 'GUEST' || !petitionPromotePageForCountry) return;

	const trafficPercentage = petitionPromotePageForCountry.traffic_percentage;
	const redirectUrl = petitionPromotePageForCountry.promote_page_route;

	if (redirectUrl && trafficPercentage >= Math.random() * 100) {
		const newPath = redirectUrl.replace(/:slug/g, encodeURIComponent(params.slug));
		if (newPath !== params.path) {
			throw new RedirectError({ url: newPath, forwardQueryParams: true });
		}
	}
}

export async function getPromotePageData(
	utilityContext: UtilityContext,
	params: PromotePageMainDataParams,
	comboPageBanditQueryParam: string | undefined,
	psfComboBanditStaticAmount: string | undefined,
): Promise<PromotePageData> {
	await handleFcmRedirect(utilityContext, params);
	const comboBanditData = getComboPageBanditData(comboPageBanditQueryParam);
	const mainData = await getPromotePageMainData(utilityContext, {
		...params,
		amountId: comboBanditData?.comboBanditData?.amountId,
		comboBanditAmount: comboBanditData?.comboBanditData?.comboBanditAmount,
	});

	return {
		...mainData,
		...comboBanditData,
		...getAmountChoices(mainData, psfComboBanditStaticAmount),
	};
}
